import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormatDate } from '../hooks/useFormatDate';
import { useShareAward } from '../hooks/useApi';
import type { Award } from '../types';
import { fetchAwardStats, type AwardStats } from '../lib/awardEvents';
import { downloadFile, safeFilename } from '../lib/downloadFile';

type ViewMode = 'owner' | 'admin';

interface AwardDetailModalProps {
  award: Award;
  onClose: () => void;
  viewMode?: ViewMode;
  /** Owner-mode only: triggered when the user clicks "Resend email". */
  onResend?: (award: Award) => void;
  /** Loading state for the resend button. */
  resendPending?: boolean;
  /** Owner-mode only: revoke this award (keeps record, marks invalid). */
  /** Owner-mode only: edit an unsent, non-revoked credential in place. */
  onEdit?: (award: Award) => void;
  onRevoke?: (award: Award) => void;
  /** Owner-mode only: hard-delete this award (mistakes). */
  onDelete?: (award: Award) => void;
}

function awardGuid(award: Award): string {
  return award.id.split('/').pop() || award.id;
}

function publicAwardUrl(award: Award): string {
  const guid = awardGuid(award);
  // Mirror src/@common/shared-badges.mjs getAwardPublicViewUrl. Hardcoded host
  // because the frontend is deployed per-environment.
  const host = window.location.hostname.includes('staging')
    ? 'https://staging.badges.ninja'
    : 'https://badges.ninja';
  return `${host}/awards/${guid}`;
}

function awardPdfUrl(award: Award): string {
  const guid = awardGuid(award);
  const apiHost = window.location.hostname.includes('staging')
    ? 'https://api-staging.badges.ninja'
    : 'https://api.badges.ninja';
  return `${apiHost}/certify-badge/award/${guid}/pdf`;
}

function linkedinAddToProfileUrl(award: Award): string | null {
  const orgId = award.badge?.issuer?.linkedinOrganizationId;
  if (!orgId) return null;
  const guid = awardGuid(award);
  const issued = award.issuedOn ? new Date(award.issuedOn) : new Date();
  const host = window.location.hostname.includes('staging')
    ? 'https://staging.badges.ninja'
    : 'https://badges.ninja';
  const verifyUrl = `${host}/verify/${guid}`;
  const params = new URLSearchParams({
    startTask: 'CERTIFICATION_NAME',
    name: award.badge?.name || '',
    organizationId: orgId,
    issueYear: String(issued.getFullYear()),
    issueMonth: String(issued.getMonth() + 1),
    certUrl: verifyUrl,
    certId: guid,
  });
  if (award.expires) {
    const exp = new Date(award.expires);
    params.set('expirationYear', String(exp.getFullYear()));
    params.set('expirationMonth', String(exp.getMonth() + 1));
  }
  return `https://www.linkedin.com/profile/add?${params.toString()}`;
}

function parseCriteria(criteria?: { narrative: string | string[] }): string[] {
  if (!criteria) return [];
  if (Array.isArray(criteria.narrative)) return criteria.narrative;
  if (typeof criteria.narrative === 'string') {
    return criteria.narrative.split(/\.\s|\n/).map(s => s.trim()).filter(Boolean);
  }
  return [];
}

export function AwardDetailModal({ award, onClose, viewMode = 'owner', onResend, resendPending, onEdit, onRevoke, onDelete }: AwardDetailModalProps) {
  const { t } = useTranslation('dashboard');
  const formatDate = useFormatDate();
  const [showShare, setShowShare] = useState(false);
  const [showIdentity, setShowIdentity] = useState(false);
  const [copied, setCopied] = useState(false);

  // Esc to close (unless a sub-modal is open).
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (showShare) { setShowShare(false); return; }
      onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [showShare, onClose]);

  const expiresMs = award.expires ? new Date(award.expires).getTime() : null;
  const now = Date.now();
  const isExpired = expiresMs !== null && expiresMs < now;
  const expiringSoon = !isExpired && expiresMs !== null && (expiresMs - now) < 30 * 24 * 60 * 60 * 1000;
  const criteria = useMemo(() => parseCriteria(award.badge?.criteria), [award.badge?.criteria]);
  const url = publicAwardUrl(award);

  // Engagement stats — fetched once when the modal opens, no auth needed.
  const [stats, setStats] = useState<AwardStats | null>(null);
  useEffect(() => {
    let cancel = false;
    fetchAwardStats(awardGuid(award)).then(s => { if (!cancel) setStats(s); });
    return () => { cancel = true; };
  }, [award]);

  function copyPublicUrl() {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-0 md:p-4">
      <div className="fixed inset-0 bg-black/40" onClick={onClose} />
      <div className="relative z-10 w-full h-full md:h-auto md:max-h-[90vh] md:max-w-4xl rounded-none md:rounded-2xl bg-white shadow-xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4 flex-shrink-0">
          <h2 className="text-base font-semibold text-gray-900">{t('awards.detail.title', 'Award Details')}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">&times;</button>
        </div>

        {award.revoked && (
          <div className="mx-6 mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-2.5 text-xs text-red-800 flex-shrink-0">
            <span className="font-semibold">{t('awards.detail.revoked', 'This credential has been revoked')}</span>
            {award.revocationReason ? ` — ${award.revocationReason}` : ''}
          </div>
        )}

        {/* Body — two columns on lg, stacked otherwise */}
        <div className="flex-1 overflow-y-auto">
          <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-6 p-6">
            {/* Left column: hero + quick actions */}
            <div className="space-y-4">
              <div
                className="rounded-xl border border-gray-200 p-4 flex items-center justify-center"
                style={{
                  backgroundImage: 'linear-gradient(45deg, #f3f4f6 25%, transparent 25%), linear-gradient(-45deg, #f3f4f6 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #f3f4f6 75%), linear-gradient(-45deg, transparent 75%, #f3f4f6 75%)',
                  backgroundSize: '16px 16px',
                  backgroundPosition: '0 0, 0 8px, 8px -8px, -8px 0px',
                }}
              >
                <img
                  src={award.image || award.badge?.image}
                  alt={award.badge?.name}
                  className="h-60 w-60 object-contain"
                  onError={e => { (e.target as HTMLImageElement).src = '/assets/badge-templates/Template-000.png'; }}
                />
              </div>
              <div className="grid grid-cols-1 gap-2">
                <button
                  type="button"
                  onClick={() => downloadFile(award.image, safeFilename(award.badge?.name || 'award', 'png'))}
                  title="Open Badges v2 baked PNG — assertion JSON embedded for offline verification"
                  className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V4" /></svg>
                  {t('awards.detail.downloadBakedPng', 'Download PNG (Open Badges baked)')}
                </button>
                <a
                  href={awardPdfUrl(award)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                  {t('awards.detail.downloadPdf', 'Download PDF certificate')}
                </a>
                <button
                  type="button"
                  onClick={copyPublicUrl}
                  className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                  {copied ? t('awards.detail.copied', 'Copied!') : t('awards.detail.copyPublicUrl', 'Copy public URL')}
                </button>
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-lg border border-primary-200 bg-primary-50 px-3 py-2 text-xs font-semibold text-primary-700 hover:bg-primary-100"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                  {t('awards.detail.openPublicPage', 'Open public page')}
                </a>
                {linkedinAddToProfileUrl(award) && (
                  <a
                    href={linkedinAddToProfileUrl(award)!}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 rounded-lg px-3 py-2 text-xs font-semibold text-white bg-[#0a66c2] hover:bg-[#004182]"
                  >
                    <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.063 2.063 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                    {t('awards.detail.addToLinkedIn', 'Add to LinkedIn')}
                  </a>
                )}
              </div>
            </div>

            {/* Right column: content */}
            <div className="space-y-5 min-w-0">
              {/* Status banner */}
              {isExpired && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                  {t('awards.detail.expiredBanner', 'Expired on {{date}}', { date: formatDate(award.expires!, 'long') })}
                </div>
              )}
              {!isExpired && expiringSoon && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  {t('awards.detail.expiringSoonBanner', 'Expires on {{date}}', { date: formatDate(award.expires!, 'long') })}
                </div>
              )}
              {!isExpired && !expiringSoon && (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
                  {t('awards.detail.activeBanner', 'Active')}
                </div>
              )}

              {/* Title block */}
              <div>
                <h3 className="text-xl font-bold text-gray-900">{award.badge?.name || t('awards.detail.untitled', 'Untitled badge')}</h3>
                <p className="mt-1 text-sm text-gray-500">
                  {t('awards.detail.issuedBy', 'Issued by')}{' '}
                  {award.badge?.issuer?.url ? (
                    <a href={award.badge.issuer.url} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">
                      {award.badge.issuer.name}
                    </a>
                  ) : (
                    <span className="text-gray-700">{award.badge?.issuer?.name || '—'}</span>
                  )}
                </p>
                <p className="mt-1 text-xs text-gray-500">
                  {t('awards.detail.issuedOn', 'Issued')} {award.issuedOn ? formatDate(award.issuedOn, 'long') : '—'}
                  {award.expires && (
                    <> · {t('awards.detail.expiresOn', 'Expires')} {formatDate(award.expires, 'long')}</>
                  )}
                </p>
              </div>

              {/* Description */}
              {award.badge?.description && (
                <div>
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-1">
                    {t('awards.detail.description', 'Description')}
                  </h4>
                  <p className="text-sm text-gray-700 leading-relaxed">{award.badge.description}</p>
                </div>
              )}

              {/* Criteria */}
              {criteria.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">
                    {t('awards.detail.criteria', 'Criteria')}
                  </h4>
                  <ul className="space-y-1.5">
                    {criteria.map((c, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                        <svg className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clipRule="evenodd" /></svg>
                        <span>{c}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Recipient */}
              <div>
                <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-1">
                  {t('awards.detail.recipient', 'Recipient')}
                </h4>
                <p className="text-sm font-medium text-gray-900">{award.recipient?.name || '—'}</p>
                {award.recipientEmail && (
                  <p className="text-xs text-gray-500">{award.recipientEmail}</p>
                )}
              </div>

              {/* Engagement (read-only stats — no per-viewer identity per Credly convention) */}
              {stats && (Object.keys(stats.totals).length > 0) && (
                <div>
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">
                    {t('awards.detail.engagement', 'Engagement')}
                  </h4>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
                    <StatTile label={t('awards.stats.views', 'Views')} value={stats.totals.view ?? 0} />
                    <StatTile label={t('awards.stats.shares', 'Shares')} value={stats.totals.share ?? 0} />
                    <StatTile label={t('awards.stats.downloads', 'Downloads')} value={stats.totals.download ?? 0} />
                    <StatTile label={t('awards.stats.linkedinAdds', 'LinkedIn adds')} value={stats.totals.click_linkedin_addtoprofile ?? 0} />
                  </div>
                  {stats.networks.share && Object.keys(stats.networks.share).length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2 text-[11px]">
                      {Object.entries(stats.networks.share).map(([net, count]) => (
                        <span key={net} className="rounded-full bg-gray-100 px-2 py-0.5 text-gray-600">
                          {net} <span className="font-semibold text-gray-800">{count}</span>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Award identity (collapsible — for verifiers / technical audience) */}
              <div className="rounded-lg border border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowIdentity(v => !v)}
                  className="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold uppercase tracking-wider text-gray-500 hover:bg-gray-50"
                >
                  <span>{t('awards.detail.identity', 'Award identity')}</span>
                  <svg className={`h-3.5 w-3.5 transition-transform ${showIdentity ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {showIdentity && (
                  <div className="border-t border-gray-100 p-3 space-y-2 text-xs font-mono">
                    <IdRow label={t('awards.detail.identityId', 'Award ID')} value={award.id} />
                    {award.recipient?.identity && (
                      <IdRow label={t('awards.detail.identityHash', 'Recipient hash')} value={award.recipient.identity} />
                    )}
                    {award.recipient?.salt && (
                      <IdRow label={t('awards.detail.identitySalt', 'Salt')} value={award.recipient.salt} />
                    )}
                    <a
                      href={award.id}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-primary-600 hover:underline"
                    >
                      {t('awards.detail.viewObJson', 'View Open Badges v2 JSON')} ↗
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-gray-100 px-6 py-3 flex-shrink-0 bg-gray-50/50">
          <div className="flex gap-2">
            {viewMode === 'owner' && onEdit && !award.notifiedAt && !award.revoked && (
              <button
                type="button"
                onClick={() => onEdit(award)}
                className="rounded-lg border border-primary-200 bg-white px-3 py-2 text-xs font-medium text-primary-700 hover:bg-primary-50"
              >
                {t('awards.detail.edit', 'Edit')}
              </button>
            )}
            {viewMode === 'owner' && onResend && (
              <button
                type="button"
                onClick={() => onResend(award)}
                disabled={resendPending}
                className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                {resendPending ? t('awards.detail.sending', 'Sending…') : t('awards.detail.resendEmail', 'Resend email')}
              </button>
            )}
            {viewMode === 'owner' && !award.revoked && (
              <button
                type="button"
                onClick={() => setShowShare(true)}
                className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50"
              >
                {t('awards.detail.shareViaEmail', 'Share via email')}
              </button>
            )}
            {viewMode === 'owner' && onRevoke && !award.revoked && (
              <button
                type="button"
                onClick={() => onRevoke(award)}
                className="rounded-lg border border-amber-200 bg-white px-3 py-2 text-xs font-medium text-amber-700 hover:bg-amber-50"
              >
                {t('awards.detail.revoke', 'Revoke')}
              </button>
            )}
            {viewMode === 'owner' && onDelete && (
              <button
                type="button"
                onClick={() => onDelete(award)}
                className="rounded-lg border border-red-100 bg-white px-3 py-2 text-xs font-medium text-red-600 hover:bg-red-50 hover:border-red-200"
              >
                {t('awards.detail.delete', 'Delete')}
              </button>
            )}
          </div>
          <button onClick={onClose} className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-xs font-medium text-gray-600 hover:bg-gray-50">
            {t('common:buttons.close', 'Close')}
          </button>
        </div>
      </div>

      {showShare && (
        <ShareSubmodal award={award} onClose={() => setShowShare(false)} />
      )}
    </div>
  );
}

function IdRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-gray-400 font-sans">{label}</div>
      <div className="text-gray-700 break-all">{value}</div>
    </div>
  );
}

function StatTile({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-2">
      <div className="text-lg font-bold text-gray-900">{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-gray-400">{label}</div>
    </div>
  );
}

/* ---------- Share via email sub-modal ---------- */
function ShareSubmodal({ award, onClose }: { award: Award; onClose: () => void }) {
  const { t } = useTranslation('dashboard');
  const { mutate: shareAward, isPending } = useShareAward();
  const [recipients, setRecipients] = useState('');
  const [subject, setSubject] = useState(t('awards.share.defaultSubject', 'Look at the {{badge}} I just awarded', { badge: award.badge?.name || 'badge' }));
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);

  // Seed the message with the badge's share-text template (substituted server-side
  // for {{recipient_name}}, {{badge_name}}, {{issuer_name}}, {{date}}, {{award_url}}).
  useEffect(() => {
    const guid = award.id.split('/').pop() || award.id;
    const apiHost = window.location.hostname.includes('staging')
      ? 'https://api-staging.badges.ninja'
      : 'https://api.badges.ninja';
    fetch(`${apiHost}/certify-badge/award/${encodeURIComponent(guid)}/share-text`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data && typeof data.text === 'string' && data.text) setMessage(data.text); })
      .catch(() => {});
  }, [award.id]);

  function handleSend() {
    setError('');
    const list = recipients.split(/[,\s]+/).map(s => s.trim()).filter(Boolean);
    if (list.length === 0) { setError(t('awards.share.noRecipients', 'Enter at least one recipient email.')); return; }
    if (list.length > 20) { setError(t('awards.share.tooMany', 'Up to 20 recipients per send.')); return; }
    shareAward(
      { awardId: awardGuid(award), recipients: list.join(','), subject, message },
      {
        onSuccess: () => { setSent(true); setTimeout(onClose, 1200); },
        onError: (err) => setError((err as Error).message),
      }
    );
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/30" onClick={onClose} />
      <div className="relative z-10 w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-gray-900">{t('awards.share.title', 'Share via email')}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-lg leading-none">&times;</button>
        </div>
        {sent ? (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-3 text-sm text-emerald-700 text-center">
            {t('awards.share.sent', 'Sent ✓')}
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                {t('awards.share.recipients', 'Recipients')}
                <span className="text-gray-400 font-normal"> {t('awards.share.recipientsHint', '(comma or space separated, up to 20)')}</span>
              </label>
              <input
                type="text"
                value={recipients}
                onChange={e => setRecipients(e.target.value)}
                placeholder="alice@example.com, bob@example.com"
                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('awards.share.subject', 'Subject')}</label>
              <input
                type="text"
                value={subject}
                onChange={e => setSubject(e.target.value)}
                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('awards.share.message', 'Message')}</label>
              <textarea
                value={message}
                onChange={e => setMessage(e.target.value)}
                rows={4}
                placeholder={t('awards.share.messagePlaceholder', 'Optional personal note...')}
                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400"
              />
            </div>
            {error && <p className="text-xs text-red-600">{error}</p>}
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={onClose} className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50">
                {t('common:buttons.cancel', 'Cancel')}
              </button>
              <button
                onClick={handleSend}
                disabled={isPending || !recipients.trim()}
                className="rounded-lg bg-primary-600 px-4 py-2 text-xs font-semibold text-white hover:bg-primary-700 disabled:opacity-50"
              >
                {isPending ? t('awards.share.sending', 'Sending…') : t('awards.share.send', 'Send')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
